import React, { useState, useEffect, useCallback, useRef } from 'react';
import Button from '../../../../composants/Button/Button';

interface OtpVerificationProps {
    candidatureId: number;
    email: string;
    telephone?: string;
    telephoneValide: boolean;
    onVerified: (token: string) => void;
    onTelephoneUpdated?: (phone: string) => void;
    onOtpSent?: () => void;
    /** Override URL for POST demander-otp (default: AC contrats endpoint) */
    sendOtpUrl?: string;
    /** Override URL for POST verifier-otp (default: AC contrats endpoint) */
    verifyOtpUrl?: string;
}

function maskEmail(email: string): string {
    const [local, domain] = email.split('@');
    if (!domain) return email;
    const visible = local.slice(0, 2);
    return `${visible}***@${domain}`;
}

function maskPhone(phone: string): string {
    if (phone.length < 6) return phone;
    return phone.slice(0, 4) + '****' + phone.slice(-2);
}

export default function OtpVerification({
    candidatureId,
    email,
    telephone,
    telephoneValide,
    onVerified,
    onTelephoneUpdated,
    onOtpSent,
    sendOtpUrl,
    verifyOtpUrl,
}: OtpVerificationProps) {
    const [canal, setCanal] = useState<'email' | 'sms'>(telephoneValide ? 'sms' : 'email');
    const [otpSent, setOtpSent] = useState(false);
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

    const [phoneInput, setPhoneInput] = useState('');
    const [phoneSaving, setPhoneSaving] = useState(false);
    const [phoneError, setPhoneError] = useState('');

    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');

    // Cooldown timer
    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
        return () => clearInterval(timer);
    }, [cooldown]);

    const getDestination = useCallback(() => {
        return canal === 'sms' ? (telephone || '') : email;
    }, [canal, telephone, email]);

    const sendOtp = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch(
                sendOtpUrl ?? `${apiUrl}/publications/ac27/contrats/${candidatureId}/demander-otp`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ canal, destination: getDestination() }),
                }
            );
            const data = await res.json();
            if (!res.ok) {
                setError(data.message || 'Erreur lors de l\'envoi du code.');
                return;
            }
            setOtpSent(true);
            setCooldown(60);
            onOtpSent?.();
        } catch {
            setError('Impossible de contacter le serveur.');
        } finally {
            setLoading(false);
        }
    }, [apiUrl, token, candidatureId, canal, getDestination, sendOtpUrl]);

    const saveTelephone = async () => {
        const normalized = phoneInput.replace(/\s/g, '');
        if (!/^(06|07)\d{8}$/.test(normalized)) {
            setPhoneError('Le numéro doit commencer par 06 ou 07 et contenir 10 chiffres.');
            return;
        }
        setPhoneSaving(true);
        setPhoneError('');
        try {
            const res = await fetch(`${apiUrl}/users/me`, {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ telephone: normalized }),
            });
            const data = await res.json();
            if (!res.ok) {
                setPhoneError(data.message || 'Erreur lors de la mise à jour.');
                return;
            }
            onTelephoneUpdated?.(normalized);
            setCanal('sms');
            setOtpSent(false);
            setCode('');
            setError('');
        } catch {
            setPhoneError('Impossible de contacter le serveur.');
        } finally {
            setPhoneSaving(false);
        }
    };

    const verifyOtp = async () => {
        if (code.length !== 6) return;
        setLoading(true);
        setError('');
        try {
            const res = await fetch(
                verifyOtpUrl ?? `${apiUrl}/publications/ac27/contrats/${candidatureId}/verifier-otp`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ code }),
                }
            );
            const data = await res.json();
            if (!res.ok) {
                setError(data.message || 'Code incorrect.');
                return;
            }
            if (data.valid && data.token_signature) {
                setSuccess(true);
                onVerified(data.token_signature);
            } else {
                setError(data.message || 'Code incorrect.');
            }
        } catch {
            setError('Impossible de vérifier le code.');
        } finally {
            setLoading(false);
        }
    };

    // Handle individual digit inputs
    const handleDigitChange = (index: number, value: string) => {
        if (!/^\d?$/.test(value)) return;
        const digits = code.split('');
        while (digits.length < 6) digits.push('');
        digits[index] = value;
        const newCode = digits.join('').slice(0, 6);
        setCode(newCode);

        // Auto-focus next
        if (value && index < 5) {
            inputsRef.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputsRef.current[index - 1]?.focus();
        }
    };

    if (success) {
        return (
            <div className="otp-section">
                <div className="otp-success">
                    <span className="otp-success-icon">✓</span>
                    <span>Identité vérifiée</span>
                </div>
            </div>
        );
    }

    return (
        <div className="otp-section">
            <div className="otp-title">
                {otpSent ? 'UN CODE VOUS A ÉTÉ ENVOYÉ' : 'VÉRIFICATION D\'IDENTITÉ'}
            </div>

            {/* Canal choice */}
            <div className="otp-canal-choice">
                <label className={`otp-canal-option ${canal === 'email' ? 'active' : ''}`}>
                    <input
                        type="radio"
                        name="otp-canal"
                        value="email"
                        checked={canal === 'email'}
                        onChange={() => { setCanal('email'); setOtpSent(false); setCode(''); setError(''); }}
                        disabled={loading}
                    />
                    <span>Par email ({maskEmail(email)})</span>
                </label>
                <label className={`otp-canal-option ${!telephoneValide ? 'disabled' : ''} ${canal === 'sms' ? 'active' : ''}`}>
                    <input
                        type="radio"
                        name="otp-canal"
                        value="sms"
                        checked={canal === 'sms'}
                        onChange={() => { setCanal('sms'); setOtpSent(false); setCode(''); setError(''); }}
                        disabled={!telephoneValide || loading}
                    />
                    <span>Par SMS {telephone ? `(${maskPhone(telephone)})` : ''}</span>
                </label>
                {!telephoneValide && (
                    <div className="otp-phone-update">
                        <p className="otp-phone-update-label">Numéro invalide pour le SMS. Saisissez un numéro mobile (06/07) :</p>
                        <div className="otp-phone-update-row">
                            <input
                                type="tel"
                                className="otp-phone-input"
                                value={phoneInput}
                                onChange={(e) => { setPhoneInput(e.target.value.replace(/[^0-9\s]/g, '')); setPhoneError(''); }}
                                placeholder="06 12 34 56 78"
                                maxLength={14}
                                disabled={phoneSaving}
                            />
                            <Button
                                variant="primary"
                                size="small"
                                value={phoneSaving ? '…' : 'Enregistrer'}
                                onClick={saveTelephone}
                                disabled={phoneSaving || !phoneInput.trim()}
                            />
                        </div>
                        {phoneError && <span className="otp-phone-error">{phoneError}</span>}
                    </div>
                )}
            </div>

            {!otpSent ? (
                <div className="otp-send-row">
                    <Button
                        type="button"
                        variant="primary"
                        value={loading ? 'Envoi…' : 'ENVOYER LE CODE'}
                        onClick={sendOtp}
                        disabled={loading}
                    />
                </div>
            ) : (
                <>
                    <div className="otp-digits-row">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <input
                                key={i}
                                ref={(el) => { inputsRef.current[i] = el; }}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                className="otp-digit-input"
                                value={code[i] || ''}
                                onChange={(e) => handleDigitChange(i, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(i, e)}
                                autoFocus={i === 0}
                            />
                        ))}
                        <Button
                            type="button"
                            variant="primary"
                            value={loading ? '…' : 'VÉRIFIER'}
                            onClick={verifyOtp}
                            disabled={code.length !== 6 || loading}
                            size="small"
                        />
                    </div>
                    <div className="otp-resend-row">
                        {cooldown > 0 ? (
                            <span className="otp-resend-cooldown">
                                Vous n'avez pas reçu ? Renvoyer ({cooldown}s)
                            </span>
                        ) : (
                            <button
                                type="button"
                                className="otp-resend-link"
                                onClick={sendOtp}
                                disabled={loading}
                            >
                                Renvoyer le code
                            </button>
                        )}
                    </div>
                </>
            )}

            {error && <div className="otp-error">{error}</div>}
        </div>
    );
}
